import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getPhoneFragment } from "oc/utils";

interface OcFormEditorTextInputProps {
  name: string;
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  inputAdornment?: React.ReactNode;
}

export const OcFormEditorTextInput: React.FC<OcFormEditorTextInputProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  inputAdornment,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (inputAdornment && value?.trim().startsWith("+36")) {
    value = value?.trim().replaceAll("+36", "");
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    switch (name) {
      case "district":
        if (/^\d*$/.test(newValue) && newValue.length <= 2) {
          onChange(event);
        }
        break;
      case "privatePhone":
      case "workMobilePhone":
        if (/^\d*$/.test(newValue.replaceAll(/\s/g, ""))) {
          getPhoneFragment(
            event,
            (e: { target: { name: string; value: string } }) => {
              onChange({
                ...event,
                target: {
                  ...event.target,
                  name: e.target.name,
                  value: e.target.value,
                },
              });
            },
            name,
            9,
            [2, 6],
          );
        }
        break;
      case "workPhone":
        if (/^\d*$/.test(newValue.replaceAll(/\s/g, ""))) {
          const format = newValue[0] === "1" ? [1, 5] : [2, 6];
          const maxLength = newValue[0] === "1" ? 8 : 9;
          getPhoneFragment(
            event,
            (e: { target: { name: string; value: string } }) => {
              onChange({
                ...event,
                target: {
                  ...event.target,
                  name: e.target.name,
                  value: e.target.value,
                },
              });
            },
            name,
            maxLength,
            format,
          );
        }
        break;
      default:
        onChange(event);
        break;
    }
  };

  const inputProps: { [key: string]: any } = {};
  if (["privatePhone", "workMobilePhone", "workPhone"].includes(name)) {
    inputProps.inputMode = "numeric";
    inputProps.pattern = "\\d*";
  }

  return (
    <Box key={name} paddingTop="36px" height="60px">
      <TextField
        id={name}
        name={name}
        label={t(label)}
        variant="outlined"
        fullWidth
        value={value}
        onChange={handleChange}
        error={!!error}
        helperText={
          name === "district" && !error && t("MY_HR_ACCEPTABLE_DISTRICT_FORMAT")
        }
        InputProps={
          inputAdornment
            ? {
              startAdornment: (
                <InputAdornment position="start">
                  {inputAdornment}
                </InputAdornment>
              ),
              ...inputProps,
            }
            : inputProps
        }
      />
      <Collapse
        in={error ? true : false}
        style={{
          color: theme.palette.primary.main,
          margin: "0",
          fontSize: "12px",
          marginTop: "3px",
          textAlign: "left",
          fontWeight: "400",
          lineHeight: "16px",
          letterSpacing: "0.4px",
          paddingLeft: "14px",
          marginBottom: "-15px",
        }}
      >
        <Box>{t(error)}</Box>
      </Collapse>
    </Box>
  );
};

export default OcFormEditorTextInput;
